import Utils from "@/utilities/Utils";
import { mapData } from "@/utilities/DataMapping";
import { IResource, Resource } from "@/model/Resource";
import { IVisitAnswer, VisitAnswer } from "@/model/VisitAnswer";
import { IRegistrationAnswer, RegistrationAnswer } from "@/model/RegistrationAnswer";
import { ILookupItem } from "./LookupItem";

export interface IVisit {
    id: string;
    code: string;
    pathwayID: string;
    forename: string;
    surname: string;
    email: string;
    plannedArrival: Date;
    companyID: string;
    companyOther: string;
    sponsorID: string;
    reason: string;
    photoResourceID: string;
    extraRegistrationAnswers: Array<IRegistrationAnswer>;
    created: Date;
    briefingStarted: Date;
    testStarted: Date;
    completed: Date;
    answerCount: number;
    wrongAnswerCount: number;
    wrongRedlineAnswerCount: number;
    allowedWrongAnswerCount: number;
    hasFailed: boolean;

    answers: Array<IVisitAnswer>;
    photoResource: IResource;
}

export class Visit implements IVisit {

    constructor(data?: IVisit) {
        if (data) this.update(data);
    }

    update(data: IVisit): Visit {
        mapData(data, {
            root: () => this,
            extraRegistrationAnswers: (data: IRegistrationAnswer) => new RegistrationAnswer(data),
            answers: (data: IVisitAnswer) => new VisitAnswer(data),
            photoResource: (data: IResource) => this.photoResource.update(data)
        });
        return this;
    }

    id: string = Utils.emptyGuidValue;
    code: string = "";
    pathwayID: string = Utils.emptyGuidValue;
    forename: string = "";
    surname: string = "";
    email: string = "";
    plannedArrival: Date = new Date(Utils.emptyDateValue);
    companyID: string = Utils.emptyGuidValue;
    companyOther: string = "";
    sponsorID: string = Utils.emptyGuidValue;
    reason: string = "";
    photoResourceID: string = Utils.emptyGuidValue;
    extraRegistrationAnswers: Array<RegistrationAnswer> = [];
    created: Date = new Date(Utils.emptyDateValue);
    briefingStarted: Date = new Date(Utils.emptyDateValue);
    testStarted: Date = new Date(Utils.emptyDateValue);
    completed: Date = new Date(Utils.emptyDateValue);
    answerCount: number = 0;
    wrongAnswerCount: number = 0;
    wrongRedlineAnswerCount: number = 0;
    allowedWrongAnswerCount: number = 0;
    hasFailed: boolean = false;

    answers: Array<VisitAnswer> = [];
    photoResource: Resource = new Resource();

    get correctCount() {
        return this.answerCount - this.wrongAnswerCount;
    }

    companyText(companyLookups:Array<ILookupItem>): string {
        if(this.companyOther) return this.companyOther;
        if(Utils.isEmptyId(this.companyID)) return "";
        return companyLookups.find(l => l.id === this.companyID)?.description ?? "(not found)";
    }

    get scoreColour(): string {
        if(this.answerCount == 0) return "#36F";
        if(this.hasFailed) return "#C33";
        return "#3B8";
    }

    get status(): string {
        const isCompleted = Utils.hasDateValue(this.completed);
        if(isCompleted && this.answerCount > 0 && !this.hasFailed) return "Passed";
        if(isCompleted && this.answerCount > 0 && this.hasFailed) return "Failed";
        if(isCompleted) return "Completed";
        if(Utils.hasDateValue(this.testStarted)) return "Test Started";
        if(Utils.hasDateValue(this.briefingStarted)) return "Briefing Started";
        if(Utils.hasDateValue(this.created)) return "Registered";
        return "None";
    }

    get isNew() {
        return Utils.isEmptyId(this.id);
    }
}
